import { BaseMixpanelTracker } from '@mathflat/shared/@common/dataTracking'
import type { AxiosError, AxiosResponse } from 'axios'
import qs from 'qs'
import { deviceType } from 'react-device-detect'
import type {
  DefaultResponse,
  PrintDefault,
  PrintError,
  ResErr,
  StrapiArrayResponse,
  StrapiError,
  StrapiSingleResponse,
} from 'src/types/common/api'

import { _mp_events } from '@/constant'

import { envUtils } from '../envUtils'
import { mixpanelClient, TeacherMixpanelClient } from './mixpanel'

const getMixpanel = () => {
  if (!mixpanelClient.initialized) return undefined
  return TeacherMixpanelClient.getMixpanel()
}

/**
 * @deprecated
 * data tracking2로 바뀌면서 API track등 다 정리하고, 이벤트들도 event.schema에 새로 정의
 */
const MIXPANEL_SIZE_LIMIT = 1000000

/**
 * @deprecated
 * data tracking2로 바뀌면서 API track등 다 정리하고, 이벤트들도 event.schema에 새로 정의
 */
const limitDataSize = (data: { responseData: any } & Record<string, any>) => {
  const dataSize = encodeURIComponent(JSON.stringify(data)).length
  if (dataSize < MIXPANEL_SIZE_LIMIT) {
    return data
  } else {
    const { responseData, ...rest } = data
    return rest
  }
}

/**
 * @deprecated
 * data tracking2로 바뀌면서 API track등 다 정리하고, 이벤트들도 event.schema에 새로 정의
 */
export class _Mixpanel extends BaseMixpanelTracker {
  constructor() {
    super(import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN, {
      track_pageview: true,
      persistence: 'localStorage',
      cookie_name: 'version3',
      cross_subdomain_cookie: false,
      autocapture: {
        click: false,
        pageview: 'url-with-path',
        scroll: true,
        submit: false,
        input: false,
        capture_text_content: false,
      },

      app_host: window.location.hostname,
      debug: false,
    })
  }

  override initialize() {
    super.initialize()
    getMixpanel()?.register({
      $device_type: deviceType,
      $user_agent: navigator.userAgent,
    })
  }

  override behaviorTrack(mpEventName: string, eventParams?: Record<string, any>) {
    getMixpanel()?.track(mpEventName, eventParams, { send_immediately: envUtils.isLocalDev })
  }

  override uiTrack<T extends MixpanelEventName>(
    eventName: T,
    eventProps?: EventPropsOfMPEvents<T>,
  ) {
    const event = _mp_events[eventName]
    const eName =
      event.eventCode.length > 0 ? `${event.eventCode}_${event.eventName}` : event.eventName

    getMixpanel()?.track(eName, eventProps ?? {}, {
      send_immediately: envUtils.isLocalDev,
    })
  }

  override apiTrack(response: AxiosResponse<DefaultResponse<any>>) {
    if (
      response.config.params &&
      'mp_event_name' in response.config.params &&
      response.config.url
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })

      if (mp_event_name) {
        getMixpanel()?.track(
          mp_event_name,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,
            responseData: response.data.data,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }
  override errorTrack({ response }: AxiosError<ResErr>) {
    if (
      response &&
      response.config.url &&
      response.config.params &&
      'mp_event_name' in response.config.params
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })
      if (mp_event_name) {
        getMixpanel()?.track(
          `${mp_event_name}_err`,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,
            responseData: response.data.code,
            responseMessage: response.data.message,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }
  override strapiTrack(
    response: AxiosResponse<StrapiArrayResponse<any>> | AxiosResponse<StrapiSingleResponse<any>>,
  ) {
    if (
      response.config.params &&
      'mp_event_name' in response.config.params &&
      response.config.url
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })
      if (mp_event_name) {
        getMixpanel()?.track(
          mp_event_name,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,

            responseData: Array.isArray(response.data.data)
              ? response.data.data.map(({ attributes }) => attributes)
              : response.data.data.attributes,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }
  override strapiErrorTrack({ response }: AxiosError<StrapiError>) {
    if (
      response &&
      response.config.url &&
      response.config.params &&
      'mp_event_name' in response.config.params
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })
      if (mp_event_name) {
        getMixpanel()?.track(
          `${mp_event_name}_err`,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,
            responseData: response.data.error.name,
            responseMessage: response.data.error.message,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }
  override printApiTrack(response: AxiosResponse<PrintDefault<any>>) {
    if (
      response &&
      response.config.url &&
      response.config.params &&
      'mp_event_name' in response.config.params
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })

      if (mp_event_name) {
        getMixpanel()?.track(
          mp_event_name,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,
            responseData: response.data.data,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }
  override printErrorTrack({ response }: AxiosError<PrintError>) {
    if (
      response &&
      response.config.url &&
      response.config.params &&
      'mp_event_name' in response.config.params
    ) {
      const { mp_event_name } = response.config.params
      const [url, _params] = response.config.url.split('?')
      const params = qs.parse(_params, { ignoreQueryPrefix: true })

      if (mp_event_name) {
        getMixpanel()?.track(
          `${mp_event_name}_err`,
          limitDataSize({
            method: response.config.method,
            requestBody: response.config.data,
            requestParams: Object.keys(params).length > 0 ? params : null,
            requestUrl: url,
            responseData: undefined,
            responseMessage: response.data.message,
            status: response.status,
          }),
          { send_immediately: envUtils.isLocalDev },
        )
      }
    }
  }

  startSessionRecording() {}
  stopSessionRecording() {}
}

/**
 * @deprecated
 * data tracking2로 바뀌면서 API track등 다 정리하고, 이벤트들도 event.schema에 새로 정의
 */
export const _mixpanelUtils = new _Mixpanel()

export type EventPropsOfMPEvents<T extends keyof typeof _mp_events> = {
  [K in T]: 'eventProps' extends keyof (typeof _mp_events)[K]
    ? (typeof _mp_events)[K]['eventProps']
    : never
}[T]

export type MixpanelEventName = keyof typeof _mp_events
